/**
 * DTOs and types for Cash Management
 */

export type CashMovementType = 'Sale' | 'Refund' | 'CashIn' | 'CashOut' | 'FloatIn' | 'FloatOut' | 'BankDeposit' | 'Withdrawal' | 'Petty Cash';
export type PaymentMethod = 'Cash' | 'Card' | 'Contactless' | 'Mobile' | 'Voucher' | 'Account';
export type TillStatus = 'Open' | 'Closed' | 'Reconciled' | 'Discrepancy';

export interface CashMovementDto {
  id: number;
  storeNumber: string;
  tillNumber?: string;
  movementType: CashMovementType;
  amount: number;
  currencyCode: string;
  timestamp: string;
  userId: string;
  username: string;
  reference?: string;
  notes?: string;
  transactionId?: string;
  documentId?: number; // Link to related document (e.g., CCTV)
  createdAtUtc: string;
}

export interface CashBalanceDto {
  storeNumber: string;
  currentBalance: number;
  expectedBalance: number;
  variance: number;
  currencyCode: string;
  lastUpdatedUtc: string;
  tillBalances: TillBalanceDto[];
}

export interface TillBalanceDto {
  tillNumber: string;
  balance: number;
  status: TillStatus;
  lastReconciliationUtc?: string;
}

export interface TillReconciliationDto {
  id: number;
  storeNumber: string;
  tillNumber: string;
  reconciliationDate: string;
  openingBalance: number;
  closingBalance: number;
  expectedClosingBalance: number;
  variance: number;
  currencyCode: string;
  reconciledBy: string;
  notes?: string;
  status: TillStatus;
  createdAtUtc: string;
  cashBreakdown?: CashBreakdownDto;
}

export interface CashBreakdownDto {
  notes: { [denomination: string]: number }; // e.g., { "50": 2, "20": 5, "10": 10 }
  coins: { [denomination: string]: number }; // e.g., { "2": 5, "1": 10, "0.50": 20 }
  totalNotes: number;
  totalCoins: number;
}

export interface CashSummaryDto {
  storeNumber: string;
  date: string;
  totalSales: number;
  totalCashSales: number;
  totalCardSales: number;
  totalRefunds: number;
  totalCashIn: number;
  totalCashOut: number;
  bankDeposits: number;
  openingBalance: number;
  closingBalance: number;
  expectedBalance: number;
  variance: number;
  currencyCode: string;
  paymentMethodBreakdown: { [method: string]: number };
}

export interface CreateCashMovementRequest {
  storeNumber: string;
  tillNumber?: string;
  movementType: CashMovementType;
  amount: number;
  reference?: string;
  notes?: string;
  transactionId?: string;
}

export interface CreateTillReconciliationRequest {
  storeNumber: string;
  tillNumber: string;
  closingBalance: number;
  notes?: string;
  cashBreakdown?: CashBreakdownDto;
}

/**
 * Helper functions
 */
export const formatCurrency = (amount: number, currencyCode: string = 'GBP'): string => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currencyCode,
  }).format(amount);
};

export const getMovementTypeColor = (type: CashMovementType): string => {
  switch (type) {
    case 'Sale':
    case 'CashIn':
    case 'FloatIn':
      return 'success';
    case 'Refund':
    case 'CashOut':
    case 'FloatOut':
    case 'Withdrawal':
      return 'error';
    case 'BankDeposit':
      return 'info';
    case 'Petty Cash':
      return 'warning';
    default:
      return 'default';
  }
};

export const getTillStatusColor = (status: TillStatus): string => {
  switch (status) {
    case 'Open':
      return 'info';
    case 'Closed':
      return 'default';
    case 'Reconciled':
      return 'success';
    case 'Discrepancy':
      return 'error';
    default:
      return 'default';
  }
};
